import {Injectable} from "@angular/core";
import {ApiService, ValidatorService} from "@amlCore/services";
import {Observable} from "rxjs";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {CheckTransaction} from "../../models";
import {CheckTransactionCode} from "../../models/transaction/checkTransactionCode.model";
import {CheckTransactionSubject} from "../../models/transaction/checkTransactionSubject.model";
import {CheckTransactionRejectWindowComponent} from "./checkTransactionRejectWindow.component";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {CheckTransactionRejectInfo} from "../../models/transaction/checkTransactionRejectInfo.model";
import {ImportDossierListResultModel} from "../../models/importDossierListResult.model";
import {CheckDayReportParamsModel} from "../../models/transaction/checkDayReportParamsModel";

@Injectable()
export class CheckTransactionsService {


  constructor(private api: ApiService,
              private fb: FormBuilder,
              private modalService: NgbModal) {

  }


  /*получение списка проверок анализа транзакций по текущему клиенту*/
  getTransactionCheckList(id: string, params: any): Observable<any> {
    return this.api.post(`/api/v1/transaction/check/get/${id}`, params);
  }

  /**
   * Получение списка анализа клиентских операций
   */
  getCheckTransactions(params: any): Observable<any> {
    return this.api.post(`/api/v1/transaction/check/get`, params, 'id');
  }


  /**
   * Экспорт результатов анализа операций
   * params - параметры фильтра
   * idDossier - при наличии для конкретного досье
   */
  exportCheckTransactionsExcel(params: any, idDossier?: string): Observable<any> {
    return this.api.byte(`/api/v1/export/check/transaction/xlsx${idDossier ? `/${idDossier}` : ''}`, params);
  }

  /**
   * Экспорт списока результатов анализа операций
   * checkTransactionsIds - список id результатов анализа операций
   */
  exportCheckTransactionListExcel(checkTransactionsIds: string[]): Observable<any> {
    return this.api.byte(`/api/v1/export/check/transaction/list/xlsx`, checkTransactionsIds);
  }



  /*
   * Метод Загрузки по Id проверки по транзакции
   */
  getCheckTransactionById(transactionId: string): Observable<any> {
    return this.api.get(`/api/v1/transaction/check/${transactionId}`);
  }

  /**
   * Блокировка результата анализа операции для редактирования
   * @param id - id  результата анализа операции
   */
  startEditCheckTransaction(id: string): Observable<any> {
    return this.api.get(`/api/v1/transaction/check/startEdit/${id}`);
  }

  /**
   * Разблокировка результата анализа операции для редактирования
   * @param id - id  результата анализа операции
   */
  stopEditCheckTransaction(id: string): void {
    this.api.get(`/api/v1/transaction/check/stopEdit/${id}`).subscribe(data => {});
  }

  /**
   * Метод завершения проверки анализа транзакции
   */
  finishCheckTransaction(transactionId: string): Observable<any> {
    return this.api.get(`/api/v1/transaction/check/finish/${transactionId}`);
  }

  /**
   * Метод продолжения клиентской проверки
   */
  continueCheckTransaction(transactionId: string): Observable<any> {
    return this.api.get(`/api/v1/transaction/check/continue/${transactionId}`);
  }

  /**
   * Сохранение проверки по операции
   */
  saveCheckTransaction(body: any): Observable<any> {
    return this.api.post(`/api/v1/transaction/check/update`, body);
  }

  checkTransactionExecutor(param: any, transactionId: string): Observable<any> {
    return this.api.post(`/api/v1/transaction/check/executor/${transactionId}`, param);
  }

  rejectCodeByIdList(param: CheckTransactionRejectInfo): Observable<any> {
    return this.api.post(`/api/v1/transaction/check/rejectCode`, param);
  }



  /**
   * Сформировать ФЭС на основе списока результатов анализа операций
   * checkTransactionsIds - список id результатов анализа операций
   */
  generateTransactionsFes(checkTransactionsIds: string[]): Observable<any> {
    return this.api.post(`/api/v1/transaction/check/generateFes`, checkTransactionsIds);
  }

  /**
   *  Информация по формированию ФЭС
   */
  generateFesInfo(): Observable<any> {
    return this.api.get(`/api/v1/transaction/check/generateFes/info`);
  }

  /**
   * Выгрузка общего отчета по результатам генерации ФЭС
   */
  downloadGenerateFesReport() {
    return this.api.byte('/api/v1/transaction/check/generateFes/download');
  }


  getRejectReason(code?: number) {
    const modal = this.modalService.open(CheckTransactionRejectWindowComponent, {
      backdrop: 'static',
      keyboard: false,
      size: 'xl',
      scrollable: true
    });
    return modal.result;
  }

  createTransactionCheckForm(data: CheckTransaction): FormGroup {
    return this.fb.group({
      basicCode: [data.basicCode, Validators.required],
      otherCodes: [data.otherCodes],
      unusualCodes: [data.unusualCodes],
      action: [data.action],
      penalty: [data.penalty, Validators.required],
      checkCodes: this.createCodesFormArray(data.checkCodes),
    });
  }

  private createCodesFormArray(data: CheckTransactionCode[]): FormArray {
    let setArray = this.fb.array([]);
    data.forEach(s=> {
      setArray.push(this.createCodeForm(s));
    })
    return setArray;
  }

  createCodeForm(data: CheckTransactionCode): FormGroup {
    return this.fb.group({
      id: [data.id],
      checkCode: [data.checkCode],
      riskClass: [data.riskClass],
      participantType: [data.participantType],
      basic: [data.basic],
      unusual: [data.unusual],
      rejected: [data.rejected],
      rejectReason:  [data.rejectReason],
      subjects: this.createSubjectsFormArray(data.subjects),
    });
  }

  private createSubjectsFormArray(data: CheckTransactionSubject[]): FormArray {
    if(!data){
      return null;
    }
    let setArray = this.fb.array([]);
    data.forEach(s=> {
      setArray.push(this.createSubjectForm(s));
    })
    return setArray;
  }

  private createSubjectForm(data: CheckTransactionSubject): FormGroup {
    return this.fb.group({
      id: [data.id, Validators.required],
      checkResult: [data.checkResult || '2', [Validators.required, this.checkResultValidator()]],
      participantType: [data.participantType],
      dossierInfo: [data.dossierInfo],
      dopInfos: [data.dopInfos],
    });
  }

  checkResultValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (['0','1'].includes(control?.value)) {
        return null;
      }
      return {
        customValidator: {
          errorMsg: 'Необходимо принять решение о совпадении полном или отсутствии.'
        }
      };
    };
  }

  dowloadDayReport(data: CheckDayReportParamsModel) {
      return this.api.byte('/api/v1/transaction/check/report/day', data);
  }
}
